import { useMutation, useQueryClient } from '@tanstack/react-query';

import { uninstallLibraryItem } from '@/lib/api/generated/api';
import type { LibraryItemKind } from '@/lib/api/generated/model';
import { invalidateWorkspaceFiles } from '@/features/workspace-files';
import { libraryKeys } from '../queryKeys';

/** The service's refusal envelope, when the body carries one. */
function refusalMessage(data: unknown, fallback: string): string {
  if (typeof data === 'object' && data !== null) {
    const message = (data as { message?: unknown }).message;
    if (typeof message === 'string' && message) return message;
  }
  return fallback;
}

/**
 * Taking an installed agent or skill back out (spec 106 US1).
 *
 * The service answers with nothing at all, which is the whole contract: what
 * the workspace holds afterwards is the catalog's to say, so the catalog is
 * refetched and the row speaks for itself.
 *
 * The files page's listings go too. An agent or a skill is a folder there, and
 * that page had already cached the listing that still contains it: without
 * this, removing a skill and opening Files showed it, until a reload.
 *
 * A refusal is the service's sentence, shown as it is: this page never argues
 * about whose copy it is (spec 106 FR-004, FR-005).
 */
export function useUninstallLibraryItem(workspaceId: string) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (input: { kind: LibraryItemKind; key: string }) => {
      const response = await uninstallLibraryItem(
        workspaceId,
        input.kind,
        input.key,
      );
      if (response.status === 204) return;
      throw new Error(
        refusalMessage(response.data, 'It could not be removed.'),
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: libraryKeys.items(workspaceId),
      });
      await invalidateWorkspaceFiles(queryClient, workspaceId);
    },
  });

  return {
    uninstall: (kind: LibraryItemKind, key: string) => {
      mutation.mutate({ kind, key });
    },
    pending: mutation.isPending,
    error: mutation.error?.message,
    reset: mutation.reset,
  };
}
